import * as React from "react";
import { createContext, Dispatch, FC, SetStateAction, useContext, useState } from "react";
import { FormInput, IFormData } from "../Forms/formFields";
import { Feature } from "ol";
import { ApplicantType } from "../Map/enums";
import { IObjectSection, ISummaryParams } from "../Popups/Registration/SummarySection";
import { useNomaContext } from "./MapContext";

export interface IRegistrationContext {
    userConsent: boolean;
    setUserConsent: Dispatch<SetStateAction<boolean>>;
    formData: IFormData;
    setFormData: Dispatch<SetStateAction<IFormData>>;
    validateForm: number;
    setValidateForm: (timeStamp: number) => void;
    isFormValid: number;
    setIsFormValid: Dispatch<SetStateAction<number>>;
    resetForm: number;
    setResetForm: Dispatch<SetStateAction<number>>;
    rawData: IFormData;
    setRawData: Dispatch<SetStateAction<IFormData>>;
    formFeature: Feature;
    setFormFeature: Dispatch<SetStateAction<Feature>>;
    applicantType: ApplicantType;
    setApplicantType: Dispatch<SetStateAction<ApplicantType>>;
    formDefaultData: IFormData;
    setFormDefaultData: Dispatch<SetStateAction<IFormData>>;
    attachments: FileList;
    setAttachments: Dispatch<SetStateAction<FileList>>;
    summaryData: ISummaryParams;
    setSummaryData: Dispatch<SetStateAction<ISummaryParams>>;
    getObjectSectionJson: () => IObjectSection;
    getSummaryJson: () => ISummaryParams;
}

const RegistrationContext = createContext<IRegistrationContext>(null);

export const useRegistrationContext = (): IRegistrationContext => useContext(RegistrationContext);

export const RegistrationContextProvider: FC = (props: React.PropsWithChildren<IRegistrationContext>) => {
    const { registerFeature, getFieldValue, getFieldAlias, getDateFormated, snapshotBig, snapshotSmall } =
        useNomaContext();

    const [userConsent, setUserConsent] = useState<boolean>(false);
    const [formData, setFormData] = useState({});
    const [validateForm, setValidateForm] = useState<number>(0);
    const [isFormValid, setIsFormValid] = useState<number>(null);
    const [resetForm, setResetForm1] = useState<number>(0);
    const [rawData, setRawData] = useState({});
    const [formFeature, setFormFeature] = useState<Feature>(null);
    const [applicantType, setApplicantType] = useState<ApplicantType>(
        registerFeature.get("LAYERID") === "1" ? ApplicantType.Company : ApplicantType.Individual
    );
    const [formDefaultData, setFormDefaultData] = useState({});
    const [attachments, setAttachments] = useState<FileList>(null);
    const [summaryData, setSummaryData] = useState<ISummaryParams>();

    const prepareToResetForm = () => {
        setRawData({});
        setFormFeature(null);
        setFormData({});
        setFormDefaultData({});
        setIsFormValid(null);
        setValidateForm(0);
        setAttachments(null);
        setResetForm1(Date.now());
    };

    const getObjectSectionJson = (): IObjectSection => {
        return {
            header: {
                headerRows:
                    registerFeature.get("LAYERID") === "1"
                        ? [
                              {
                                  alias: "",
                                  value: getFieldValue(registerFeature, "PROPERTYTITLE", "1")
                              },
                              {
                                  alias: getFieldAlias("MW", "1") + ": ",
                                  value: getFieldValue(registerFeature, "MW", "1")
                              }
                          ]
                        : [
                              {
                                  alias: "LVM reģions: ",
                                  value: getFieldValue(registerFeature, "LVMFORESTRYCODE")
                              },
                              {
                                  alias: "",
                                  value:
                                      getFieldValue(registerFeature, "REGIONATVK") +
                                      ", " +
                                      getFieldValue(registerFeature, "MUNICIPALITYATVK") +
                                      ", " +
                                      getFieldValue(registerFeature, "PROPERTYTITLE")
                              },
                              {
                                  alias: getFieldAlias("PROPERTYCADASTRALNUMBER") + ": ",
                                  value: getFieldValue(registerFeature, "PROPERTYCADASTRALNUMBER")
                              },
                              {
                                  alias: getFieldAlias("CADASTER") + ": ",
                                  value: getFieldValue(registerFeature, "CADASTER")
                              }
                          ],
                snapshotBig: snapshotBig,
                snapshotSmall: snapshotSmall
            },
            objectInfoRows: [
                {
                    alias: getFieldAlias("LANDAREA") + ": ",
                    value: getFieldValue(registerFeature, "LANDAREA")
                },
                {
                    alias: getFieldAlias("RENTPURPOSE") + ": ",
                    value: getFieldValue(registerFeature, "RENTPURPOSE")
                },
                !registerFeature || registerFeature.get("TYPEOFLEASEGRANTING") !== 5
                    ? {
                          alias: getFieldAlias("STARTRENT") + ": ",
                          value: getFieldValue(registerFeature, "STARTRENT")
                      }
                    : undefined,
                {
                    alias: getFieldAlias("AGREEMENTTERM") + ": ",
                    value: getFieldValue(registerFeature, "AGREEMENTTERM")
                }
            ]
        };
    };

    const getSummaryJson = () => {
        const typeOfLease = registerFeature.get("TYPEOFLEASEGRANTING");
        const note =
            typeOfLease === 1
                ? "Nomas pieteikumu LVM apstrādās un izvērtēs atbilstoši normatīvo aktu prasībām par publiskas personas zemes nomu. Pieteikums negarantē nomas tiesību ieguvi"
                : typeOfLease === 5
                ? "Nomas pieteikumu LVM apstrādās un izvērtēs atbilstoši normatīvo aktu prasībām par publiskas personas zemes nomu. Pēc pieteikuma apstrādes LVM precizēs nomas maksu un citus iznomāšanas nosacījumus. Par procesa tālāku virzību ar Jums sazināsies attiecīgā reģiona Nekustamo īpašumu pārvaldes Zemes lietojuma speciālists"
                : null;

        const attachedFiles = attachments
            ? {
                  title: "Pievienotie dokumenti:",
                  rows: Array.from(attachments, (attachment) => attachment.name)
              }
            : undefined;

        return {
            ...getObjectSectionJson(),
            globalId: "Id: " + getFieldValue(formFeature, "GLOBALID"),
            customer: {
                title: "Pieteicēja dati:",
                rows: [
                    applicantType === ApplicantType.Individual
                        ? formData[FormInput.Name] + " " + formData[FormInput.Surname]
                        : formData[FormInput.Company],
                    applicantType === ApplicantType.Individual
                        ? formData[FormInput.PersonalCode].toString()
                        : formData[FormInput.RegistrationNumber].toString(),
                    formData[FormInput.Address].toString(),
                    formData[FormInput.Phone].toString(),
                    formData[FormInput.Email].toString(),
                    formData[FormInput.Notes]
                ]
            },
            attachments: attachedFiles,
            date: "Datums: " + getDateFormated(Date.now()),
            visiting: {
                alias: "Par objekta apskati vienoties ar kontaktpersonu: ",
                value:
                    getFieldValue(registerFeature, "EMPLOYEENAME") +
                    " (" +
                    getFieldValue(registerFeature, "EMPLOYEEEMAIL") +
                    "; " +
                    getFieldValue(registerFeature, "EMPLOYEEPHONE") +
                    ")"
            },
            footerNote: note
        };
    };

    const values = {
        userConsent,
        setUserConsent,
        formData,
        setFormData,
        validateForm,
        setValidateForm,
        isFormValid,
        setIsFormValid,
        resetForm,
        setResetForm: prepareToResetForm,
        rawData,
        setRawData,
        formFeature,
        setFormFeature,
        applicantType,
        setApplicantType,
        formDefaultData,
        setFormDefaultData,
        attachments,
        setAttachments,
        summaryData,
        setSummaryData,
        getObjectSectionJson,
        getSummaryJson
    };

    return <RegistrationContext.Provider value={values}>{props.children}</RegistrationContext.Provider>;
};
